import type { OnInit } from '@angular/core';
import { Component, ViewChild, computed, inject, signal } from '@angular/core';

import {
  AvatarComponent, BadgeComponent, ButtonComponent, CardComponent,
  ConfirmDialogComponent, EmptyStateComponent, SearchBarComponent,
  SpinnerComponent, ToastService,
} from '@echospaces-web-monorepo/es-shared-ui';
import {
  CompanyMembersService,
  type CompanyMemberRole, type OrgMember,
} from '@echospaces-web-monorepo/es-portal-ecms';

import { InviteMemberModalComponent } from './invite-member-modal.component';

const ROLE_VARIANT: Record<CompanyMemberRole, 'primary' | 'secondary' | 'danger'> = {
  staff: 'secondary',
  editors: 'primary',
  'super-admin': 'danger',
};

/**
 * Customer-Super-Admin's member management page. Lists every member of
 * the calling user's organization with full CRUD: invite (creates a new
 * Keycloak user if needed), toggle role flags inline, remove.
 *
 * `staff` is the floor role every member has — it's not shown as a chip
 * because every row would have it. The chips show only opt-in roles
 * (`editors`, `super-admin`).
 */
@Component({
  selector: 'es-admin-members-page',
  imports: [
    AvatarComponent, BadgeComponent, ButtonComponent, CardComponent,
    ConfirmDialogComponent, EmptyStateComponent, SearchBarComponent,
    SpinnerComponent, InviteMemberModalComponent,
  ],
  template: `
    <div class="max-w-5xl mx-auto">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold font-exo">Members</h1>
          <p class="text-sm text-[var(--text-secondary)]">
            {{ filtered().length }} members in your organization
          </p>
        </div>
        <es-button variant="primary" (clicked)="inviteOpen.set(true)">+ Invite member</es-button>
      </div>

      <es-search-bar
        class="mb-4 block"
        placeholder="Search by name or email…"
        [(value)]="searchQuery"
      ></es-search-bar>

      @if (members.loading()) {
        <div class="flex justify-center py-12">
          <es-spinner label="Loading members…"></es-spinner>
        </div>
      } @else if (filtered().length === 0) {
        <es-empty-state
          title="No members"
          [description]="searchQuery() ? 'No members match your search.' : 'Invite the first member to get started.'"
          icon="👥"
          [actionLabel]="searchQuery() ? undefined : 'Invite member'"
          (action)="inviteOpen.set(true)"
        ></es-empty-state>
      } @else {
        <div class="space-y-3">
          @for (member of filtered(); track member.userId) {
            <es-card>
              <div class="flex items-center gap-3">
                <es-avatar [name]="member.fullName ?? member.email" size="md"></es-avatar>
                <div class="flex-1 min-w-0">
                  <p class="font-medium truncate">{{ member.fullName ?? member.email }}</p>
                  @if (member.fullName) {
                    <p class="text-xs text-[var(--text-secondary)] truncate">{{ member.email }}</p>
                  }
                </div>

                <div class="flex items-center gap-1.5 flex-wrap">
                  @for (role of optInRoles(member); track role) {
                    <es-badge [label]="role" [variant]="roleVariant(role)"></es-badge>
                  }
                </div>

                <button
                  type="button"
                  class="text-xs text-[var(--text-secondary)] hover:text-[var(--text)] px-2 py-1 cursor-pointer"
                  [disabled]="updatingId() === member.userId"
                  (click)="toggleEditor(member)"
                >
                  {{ updatingId() === member.userId ? '…' : (hasRole(member, 'editors') ? 'Demote editor' : 'Make editor') }}
                </button>

                <button
                  type="button"
                  class="text-xs text-[var(--text-secondary)] hover:text-[var(--text)] px-2 py-1 cursor-pointer"
                  [disabled]="updatingId() === member.userId"
                  (click)="toggleSuperAdmin(member)"
                >
                  {{ updatingId() === member.userId ? '…' : (hasRole(member, 'super-admin') ? 'Demote admin' : 'Make admin') }}
                </button>

                <es-button
                  variant="ghost"
                  size="sm"
                  [disabled]="updatingId() === member.userId"
                  (clicked)="askRemove(member)"
                >Remove</es-button>
              </div>
            </es-card>
          }
        </div>
      }

      <es-invite-member-modal
        #inviteModal
        [open]="inviteOpen()"
        (closed)="inviteOpen.set(false)"
        (submitted)="onInviteSubmit($event)"
      ></es-invite-member-modal>

      @if (invitedEmail(); as email) {
        <div class="fixed bottom-6 right-6 max-w-md p-4 rounded-lg bg-[var(--bg-card)] border border-echo-blue/40 shadow-2xl">
          <p class="text-sm font-semibold mb-1">Invite sent</p>
          <p class="text-xs text-[var(--text-secondary)] mb-2">
            {{ email }} has been emailed a link to verify their address and set a password.
            If the address was wrong, use "Reissue invite" — possible until they first sign in.
          </p>
          <div class="flex justify-end mt-2">
            <es-button variant="ghost" size="sm" (clicked)="invitedEmail.set(null)">Dismiss</es-button>
          </div>
        </div>
      }

      @if (memberToRemove(); as target) {
        <es-confirm-dialog
          [open]="true"
          title="Remove member?"
          [message]="'Remove ' + (target.fullName ?? target.email) + ' from your organization? They will lose access immediately.'"
          confirmLabel="Remove"
          [destructive]="true"
          (confirmed)="confirmRemove(target)"
          (cancelled)="memberToRemove.set(null)"
        ></es-confirm-dialog>
      }
    </div>
  `,
})
export class AdminMembersPageComponent implements OnInit {
  @ViewChild('inviteModal') inviteModalRef?: InviteMemberModalComponent;

  readonly members = inject(CompanyMembersService);
  private readonly toast = inject(ToastService);

  readonly searchQuery = signal('');
  readonly inviteOpen = signal(false);
  readonly memberToRemove = signal<OrgMember | null>(null);
  readonly updatingId = signal<string | null>(null);
  readonly invitedEmail = signal<string | null>(null);

  readonly filtered = computed<OrgMember[]>(() => {
    const q = this.searchQuery().trim().toLowerCase();
    const all = this.members.members();
    if (!q) return all;
    return all.filter((m) =>
      m.email.toLowerCase().includes(q)
      || (m.fullName?.toLowerCase().includes(q) ?? false));
  });

  ngOnInit(): void {
    this.members.refresh();
  }

  hasRole(member: OrgMember, role: CompanyMemberRole): boolean {
    return member.roles.includes(role);
  }

  optInRoles(member: OrgMember): CompanyMemberRole[] {
    return member.roles.filter((r) => r !== 'staff');
  }

  roleVariant(role: CompanyMemberRole) {
    return ROLE_VARIANT[role];
  }

  async toggleEditor(member: OrgMember): Promise<void> {
    await this.toggleRole(member, 'editors');
  }

  async toggleSuperAdmin(member: OrgMember): Promise<void> {
    await this.toggleRole(member, 'super-admin');
  }

  private async toggleRole(member: OrgMember, role: CompanyMemberRole): Promise<void> {
    const has = this.hasRole(member, role);
    const next = has
      ? member.roles.filter((r) => r !== role && r !== 'staff')
      : [...member.roles.filter((r) => r !== 'staff'), role];
    this.updatingId.set(member.userId);
    try {
      await this.members.setMemberRoles(member.userId, next);
      this.toast.success(has ? `Removed ${role}` : `Granted ${role}`);
    } catch {
      this.toast.error('Failed to update roles');
    } finally {
      this.updatingId.set(null);
    }
  }

  async onInviteSubmit(payload: { email: string; fullName?: string; roles: ReadonlyArray<CompanyMemberRole> }) {
    try {
      const result = await this.members.addMember(payload);
      this.inviteModalRef?.finishSubmit();
      this.toast.success(`Invited ${result.email}`);
      this.inviteOpen.set(false);
      if (result.invited) {
        this.invitedEmail.set(result.email);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invite failed';
      this.inviteModalRef?.finishSubmit(message);
    }
  }

  askRemove(member: OrgMember): void {
    this.memberToRemove.set(member);
  }

  async confirmRemove(member: OrgMember): Promise<void> {
    this.memberToRemove.set(null);
    try {
      await this.members.removeMember(member.userId);
      this.toast.success('Member removed');
    } catch {
      this.toast.error('Failed to remove member');
    }
  }
}
